/**
 * @fileoverview Debounced-value hook.
 * @description Holds back a fast-changing value (typically a search box's text) until it has
 * stopped changing for a moment, so a server-side search doesn't send one request per keystroke.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect, useState } from 'react'

const DEFAULT_DELAY_MS = 300

/**
 * @description Debounces a value.
 * @param {T} value - The live value.
 * @param {number} [delayMs] - How long it must stay unchanged. Defaults to 300 ms.
 * @returns {T} The value once it has stopped changing for delayMs.
 */
export function useDebouncedValue<T>(value: T, delayMs = DEFAULT_DELAY_MS): T {
  const [debounced, setDebounced] = useState(value)

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(timer)
  }, [value, delayMs])

  return debounced
}
